/**
 * AI Summary Bar Component
 * Shows AI-powered communication summary with sentiment and insights
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bot, ChevronRight, Loader2, RefreshCw, Phone, AlertCircle, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { auth } from '../../../utils/auth';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import type { Message, AISummary, ClientData } from './types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AISummaryBarProps {
  caseId: string;
  client: ClientData | null;
  communications: Message[];
  expanded: boolean;
  onToggle: () => void;
}

export const AISummaryBar: React.FC<AISummaryBarProps> = ({ 
  caseId, 
  client, 
  communications, 
  expanded, 
  onToggle 
}) => {
  const { t } = useTranslation('caseWorkspace');
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [loading, setLoading] = useState(false);
  
  const needsResponseCount = useMemo(() => 
    communications.filter((c: Message) => 
      (c.direction === 'INCOMING' || c.direction === 'INBOUND') && !c.responded
    ).length
  , [communications]);
  
  const generateSummary = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/communications/ai/summary`,
        { case_id: caseId, type: 'quick' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to generate summary:', error);
      setSummary({
        status_line: needsResponseCount > 0 
          ? `${needsResponseCount} ${t('communication.waitingForResponse')}` 
          : t('communication.noUnreadMessages'),
        summary: undefined
      });
    } finally {
      setLoading(false);
    }
  }, [caseId, needsResponseCount, t]);
  
  useEffect(() => {
    if (communications.length > 0) {
      generateSummary();
    }
  }, [communications.length, generateSummary]);

  // Collapsed view
  if (!expanded) {
    return (
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl flex items-center gap-4 hover:border-gray-300 hover:shadow-md transition-all duration-200"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-xs text-gray-400 font-medium mb-0.5">{t('communication.aiSummary')}</div>
          <span className="text-sm text-gray-700 font-medium">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('communication.analyzing')}
              </span>
            ) : (
              <>{summary?.status_line || `${needsResponseCount} ${t('communication.waitingForResponse')}`}</>
            )}
          </span>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </button>
    );
  }

  // Expanded view
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <span className="flex-1 text-left text-sm font-semibold text-gray-900">
          {t('communication.aiSummary')}
          {summary?.ai_powered && (
            <span className="ml-2 text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">AI</span>
          )}
        </span>
        <ChevronRight className="w-5 h-5 text-gray-400 rotate-90" />
      </button>
      
      <div className="px-5 pb-5 space-y-4">
        {loading ? (
          <div className="flex items-center gap-3 text-sm text-gray-500 py-4">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('communication.analyzingWithAI')}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 leading-relaxed">
              {summary?.summary || t('communication.noSummaryYet')}
            </p>
            
            {/* Sentiment */}
            {summary?.sentiment && (
              <div className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium",
                (summary.sentiment === 'positiv' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'positiv'))
                  ? "bg-green-100 text-green-700"
                  : (summary.sentiment === 'negativ' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'negativ'))
                  ? "bg-red-100 text-red-700"
                  : (summary.sentiment === 'utålmodig' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'utålmodig'))
                  ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-600"
              )}>
                {(summary.sentiment === 'positiv' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'positiv')) ? '😊' 
                  : (summary.sentiment === 'negativ' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'negativ')) ? '😟'
                  : (summary.sentiment === 'utålmodig' || (typeof summary.sentiment === 'object' && summary.sentiment?.label === 'utålmodig')) ? '⏰'
                  : '😐'} 
                {t('communication.clientMood', 'Client')}: {typeof summary.sentiment === 'string' ? summary.sentiment : summary.sentiment?.label || t('communication.neutral', 'neutral')}
              </div>
            )}
            
            {/* Client questions */}
            {summary?.client_questions && summary.client_questions.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {t('communication.clientQuestions', "Client's unanswered questions")}:
                </p>
                <ul className="text-sm text-amber-700 space-y-1.5">
                  {summary.client_questions.slice(0, 3).map((q, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-amber-400 mt-0.5">•</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Obligations */}
            {summary?.your_obligations && summary.your_obligations.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {t('communication.yourObligations', 'Your obligations')}:
                </p>
                <ul className="text-xs text-blue-700 space-y-1">
                  {summary.your_obligations.map((obl, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-blue-500 mt-0.5">•</span>
                      {typeof obl === 'string' ? obl : obl.task}
                      {typeof obl === 'object' && obl.deadline && (
                        <span className="text-blue-500 ml-1">(frist: {obl.deadline})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Next step */}
            {summary?.suggested_next_step && (
              <div className="flex items-center gap-2 text-xs text-violet-700 bg-violet-100/50 rounded-lg px-2.5 py-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                <span><strong>Anbefaling:</strong> {summary.suggested_next_step}</span>
              </div>
            )}
          </>
        )}
        
        <div className="flex gap-2 pt-1">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={generateSummary}>
            <RefreshCw className="w-3 h-3 mr-1" />
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs"
            onClick={() => toast.info(t('common.comingSoon', 'Coming soon'))}
          >
            <Phone className="w-3 h-3 mr-1" />
            {t('communication.callPrep', 'Call prep')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AISummaryBar;
